// ============================================================================
//  NotificationDropdown.tsx — Centre de notifications in-app
//  Dropdown dans le Header : badge unread, liste, marquer lu, voir tout
// ============================================================================
import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, CheckCheck, Trash2, ExternalLink } from 'lucide-react'
import { notificationsApi, type Notification } from '@/modules/notifications/api/notifications.api'

const TYPE_ICONS: Record<string, string> = {
  leave_request: '🏖️',
  ticket: '🎫',
  task_assigned: '✅',
  expense_to_validate: '🧾',
  expense_to_pay: '💸',
  MEETING_INVITED: '📅',
  MEETING_UPDATED: '📅',
  MEETING_CANCELLED: '📅',
  MEETING_POSTPONED: '📅',
  MEETING_REMINDER: '📅',
  leave_status: '🏖️',
  SIGNATURE_REQUESTED: '✍️',
  ACTION_ASSIGNED: '✅',
}

const TYPE_COLORS: Record<string, string> = {
  leave_request: '#7C3AED', // = --brand-orange en hex (le fond ajoute un suffixe d'opacité)
  ticket: '#f59e0b',
  task_assigned: '#10b981',
  expense_to_validate: '#f59e0b',
  expense_to_pay: '#10b981',
  MEETING_INVITED: '#3b82f6',
  MEETING_UPDATED: '#3b82f6',
  MEETING_CANCELLED: '#ef4444',
  MEETING_POSTPONED: '#3b82f6',
  MEETING_REMINDER: '#f97316',
  leave_status: '#7C3AED', // = --brand-orange en hex (le fond ajoute un suffixe d'opacité)
  SIGNATURE_REQUESTED: '#8b5cf6',
  ACTION_ASSIGNED: '#10b981',
}

const MODULE_ROUTES: Record<string, string> = {
  hr: '/hr/leaves',
  tickets: '/tickets',
  projects: '/projects',
  depenses: '/depenses',
  meetings: '/meetings',
  documents: '/documents',
}

/** `data` peut arriver en objet ou, sur d'anciennes lignes, en chaîne JSON. */
function parseNotifData(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
    } catch {
      return null
    }
  }
  return typeof raw === 'object' ? (raw as Record<string, unknown>) : null
}

/**
 * Chemin interne sûr, ou null. Refuse tout ce qui pourrait sortir de l'application :
 * barre oblique inverse, caractères de contrôle ou espaces (retirés/normalisés par le
 * parseur d'URL, ex. « /\evil.com » ou « /<TAB>/evil.com » → « //evil.com »), puis
 * vérifie que l'URL résolue reste sur la même origine.
 */
function internalPath(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.startsWith('/')) return null
  if (/[\\\u0000-\u001f\u007f\s]/.test(raw)) return null
  try {
    const u = new URL(raw, window.location.origin)
    if (u.origin !== window.location.origin) return null
    return u.pathname + u.search + u.hash
  } catch {
    return null
  }
}

/** Cible de navigation : lien interne explicite, sinon route du module, sinon aucune. */
function notifTarget(data: Record<string, unknown> | null): string | null {
  if (!data) return null
  const link = internalPath(data.link)
  if (link) return link
  const module = typeof data.module === 'string' ? data.module : ''
  if (!module || !MODULE_ROUTES[module]) return null
  // Dépenses : ouvre directement la fiche concernée quand elle est connue.
  if (module === 'depenses' && typeof data.expenseId === 'string' && data.expenseId) {
    return `${MODULE_ROUTES[module]}/${data.expenseId}`
  }
  return MODULE_ROUTES[module]
}

function fmtAgo(d: string) {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000)
  if (s < 60) return 'à l\'instant'
  if (s < 3600) return `il y a ${Math.floor(s / 60)}min`
  if (s < 86400) return `il y a ${Math.floor(s / 3600)}h`
  if (s < 604800) return `il y a ${Math.floor(s / 86400)}j`
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

export default function NotificationDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const qc = useQueryClient()

  // Unread count — poll every 30s
  const { data: countData } = useQuery({
    queryKey: ['notif-unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 30000,
    retry: 1,
  })
  const unreadCount = (countData as any)?.count ?? (countData as any)?.data?.count ?? 0

  // Notifications list — only fetch when open
  const { data: listData, isLoading } = useQuery({
    queryKey: ['notif-list'],
    queryFn: () => notificationsApi.list({ limit: 20 }),
    enabled: open,
    refetchInterval: open ? 15000 : false,
  })
  const raw = listData?.data ?? listData
  const notifs: Notification[] = Array.isArray(raw) ? raw : (raw as any)?.data ?? []

  // Mutations
  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notif-list'] }); qc.invalidateQueries({ queryKey: ['notif-unread-count'] }) },
  })
  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notif-list'] }); qc.invalidateQueries({ queryKey: ['notif-unread-count'] }) },
  })
  const removeNotif = useMutation({
    mutationFn: (id: string) => notificationsApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notif-list'] }); qc.invalidateQueries({ queryKey: ['notif-unread-count'] }) },
  })

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleClick = (n: Notification) => {
    if (!n.isRead) markRead.mutate(n.id)
    const target = notifTarget(parseNotifData(n.data))
    if (target) {
      navigate(target)
      setOpen(false)
    }
  }

  return (
    <div ref={ref} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="relative w-8 h-8 rounded-lg border-0 bg-transparent flex items-center justify-center cursor-pointer text-[var(--tx3)] hover:bg-[var(--surface-2)] hover:text-[var(--tx)] transition-all"
      >
        <Bell size={15} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#ef4444] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-[380px] max-h-[520px] flex flex-col rounded-xl overflow-hidden bg-white border border-[var(--bd)] shadow-[0_12px_40px_rgba(15,12,29,0.15),0_2px_8px_rgba(15,12,29,0.06)] nx-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bd)]">
            <div className="flex items-center gap-2">
              <h3 className="text-[13px] font-bold text-[var(--tx)]">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#ef4444] text-white">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="flex items-center gap-1 text-[11px] font-semibold text-[var(--blue)] hover:underline border-0 bg-transparent cursor-pointer"
              >
                <CheckCheck size={12} /> Tout marquer lu
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="py-12 text-center text-[12px] text-[var(--tx3)]">Chargement…</div>
            ) : notifs.length === 0 ? (
              <div className="py-16 text-center">
                <Bell size={32} className="mx-auto text-[var(--tx4)] mb-2 opacity-40" />
                <p className="text-[12px] text-[var(--tx3)]">Aucune notification</p>
              </div>
            ) : (
              notifs.map((n) => (
                <div
                  key={n.id}
                  className={`flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-[var(--bd)] last:border-0 transition-colors group ${
                    n.isRead
                      ? 'bg-white hover:bg-[var(--surface-2)]'
                      : 'bg-[rgba(245,124,0,0.04)] hover:bg-[rgba(245,124,0,0.08)]'
                  }`}
                  onClick={() => handleClick(n)}
                >
                  {/* Icon */}
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm"
                    style={{ background: (TYPE_COLORS[n.type] ?? '#7C3AED') + '15' }}
                  >
                    {TYPE_ICONS[n.type] ?? '🔔'}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-[12.5px] leading-snug ${n.isRead ? 'text-[var(--tx2)]' : 'text-[var(--tx)] font-semibold'}`}>
                      {n.title}
                    </p>
                    <p className="text-[11.5px] text-[var(--tx3)] mt-0.5 line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-[var(--tx4)] mt-1">{fmtAgo(n.createdAt)}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!n.isRead && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markRead.mutate(n.id) }}
                        className="w-6 h-6 rounded-md border-0 bg-transparent flex items-center justify-center cursor-pointer text-[var(--tx3)] hover:bg-[var(--surface-2)] hover:text-[var(--blue)]"
                        title="Marquer comme lu"
                      >
                        <Check size={12} />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); removeNotif.mutate(n.id) }}
                      className="w-6 h-6 rounded-md border-0 bg-transparent flex items-center justify-center cursor-pointer text-[var(--tx3)] hover:bg-[var(--red-bg)] hover:text-[var(--red)]"
                      title="Supprimer"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>

                  {/* Unread dot */}
                  {!n.isRead && (
                    <span className="w-2 h-2 rounded-full bg-[var(--blue)] shrink-0 mt-1.5" />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifs.length > 0 && (
            <div className="border-t border-[var(--bd)] px-4 py-2">
              <p className="text-[10px] text-[var(--tx4)] text-center">
                {(listData as any)?.meta?.total ?? (listData as any)?.data?.meta?.total ?? notifs.length} notification(s) au total
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
